import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import React from "react";
import { Box, IconButton, Tooltip, CircularProgress } from "@mui/joy";
import { SAddDocument, SAnalytics, SDelete, SView } from "../../../styles/StyledMUI";
import { colorTokens } from "../../../styles/globalStyles";
import DarkTypography from "../DarkTypography";
import FollowUpSummaryModal from "../../shared/followup/FollowUpSummaryModal";
import type { Training } from "api/training/Training.typed";

interface SheetTypeComponentProps {
  uploadFile: (event: any, type: string) => Promise<void>;
  deleteFile: (fileType: string) => Promise<void>;
  handleView: (type: string) => void;
  document: string | undefined;
  documentType: string;
  training: Training;
  setTrainings: Dispatch<SetStateAction<Training[] | undefined>>;
  refreshTrainings: RefreshTrainingFunction;
}

type RefreshFunction<T> = () => Promise<T>;
type RefreshFunctionWithId<T> = (id?: string) => Promise<T>;

type RefreshTrainingFunction =
  | RefreshFunction<Training[] | undefined>
  | RefreshFunctionWithId<Training[] | undefined>;

function SheetTypeComponent({
  uploadFile,
  deleteFile,
  handleView,
  document,
  documentType,
  training,
  setTrainings,
  refreshTrainings,
}: SheetTypeComponentProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [summaryOpen, setSummaryOpen] = React.useState<boolean>(false);

  const handleUpload = React.useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setIsLoading(true);
      uploadFile(e, documentType).then(() => setIsLoading(false));
    },
    [uploadFile, documentType],
  );

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${colorTokens.border.default}`,
          borderRadius: "8px",
          padding: 2,
          textAlign: "center",
          width: "100%",
          backgroundColor: colorTokens.bg.surface,
        }}
      >
        <DarkTypography level="title-md" colorVariant="title" sx={{ mb: 1, display: "block" }}>
          Fiche Suivi
        </DarkTypography>

        {!isLoading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {training.FollowUpSheet !== undefined && (
                <Tooltip title="Voir synthèse">
                  <IconButton
                    onClick={() => setSummaryOpen(true)}
                    variant="plain"
                    sx={{ color: colorTokens.text.primary }}
                  >
                    <SAnalytics sx={{ color: "inherit" }} />
                  </IconButton>
                </Tooltip>
              )}
              <label>
                <Box
                  component="input"
                  type="file"
                  onChange={handleUpload}
                  accept="application/pdf"
                  sx={{ display: "none" }}
                />
                <Tooltip title="Ajouter document">
                  <IconButton
                    component="span"
                    variant="plain"
                    sx={{ color: colorTokens.text.primary }}
                  >
                    <SAddDocument sx={{ color: "inherit" }} />
                  </IconButton>
                </Tooltip>
              </label>
            </Box>

            {document ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Tooltip title="Voir document">
                  <IconButton
                    onClick={() => handleView(documentType)}
                    variant="plain"
                    sx={{ color: colorTokens.text.primary }}
                  >
                    <SView sx={{ color: "inherit" }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Supprimer document">
                  <IconButton
                    onClick={() => deleteFile(documentType)}
                    variant="plain"
                    sx={{ color: colorTokens.status.danger }}
                  >
                    <SDelete sx={{ color: "inherit" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : null}
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <CircularProgress size="sm" sx={{ color: colorTokens.accent.primary }} />
          </Box>
        )}
      </Box>

      <FollowUpSummaryModal
        open={summaryOpen}
        onClose={() => setSummaryOpen(false)}
        customerId={training.CustomerID}
        templateId={training.TrainingTemplateID}
        setTrainings={setTrainings}
        refreshTrainings={refreshTrainings}
      />
    </>
  );
}

export default React.memo(SheetTypeComponent);
